import { KeyNamesEnum } from '../../../../constants';

export type DigitCell = number | null;

export interface DigitCellsState {
  cells: DigitCell[];
  activeIndex: number;
}

const DIGIT_REGEXP = /^\d$/;

export function joinCells(cells: DigitCell[]): string {
  return cells.filter((cell) => cell !== null).join('');
}

export function toCells(value: string, count: number): DigitCell[] {
  const digits = value.replace(/\D/g, '').split('').slice(0, count).map(Number);

  return Array.from({ length: count }, (_, i) => digits[i] ?? null);
}

/**
 * The model is the joined string, so an empty cell in the middle collapses when joined.
 * The previous cells are kept while they still join to the model, so our own writes don't
 * re-derive them; a value set from outside (or a new count) rebuilds them.
 */
export function reconcileCells(
  previous: DigitCell[] | undefined,
  value: string,
  count: number,
): DigitCell[] {
  return previous && previous.length === count && joinCells(previous) === value
    ? previous
    : toCells(value, count);
}

/** Where focus starts for freshly derived cells: the last cell once anything is filled. */
export function initialActiveIndex(cells: DigitCell[]): number {
  return cells.some((cell) => cell !== null) ? cells.length - 1 : 0;
}

export function setCell(cells: DigitCell[], index: number, digit: DigitCell): DigitCell[] {
  return cells.map((cell, i) => (i === index ? digit : cell));
}

/** Moves by `step`, staying put when that would leave the cells. */
export function moveIndex(index: number, step: number, count: number): number {
  const next = index + step;

  return next >= 0 && next < count ? next : index;
}

/**
 * The result of a key press on the active cell: a digit fills it and moves right, Backspace
 * clears it and moves left, arrows only move. Any other key leaves the state as it is.
 */
export function reduceKeyDown(state: DigitCellsState, key: string, count: number): DigitCellsState {
  const { cells, activeIndex } = state;

  if (DIGIT_REGEXP.test(key)) {
    return {
      cells: setCell(cells, activeIndex, parseInt(key)),
      activeIndex: moveIndex(activeIndex, 1, count),
    };
  }
  if (key === KeyNamesEnum.ArrowLeft) {
    return { cells, activeIndex: moveIndex(activeIndex, -1, count) };
  }
  if (key === KeyNamesEnum.ArrowRight) {
    return { cells, activeIndex: moveIndex(activeIndex, 1, count) };
  }
  if (key === KeyNamesEnum.Backspace) {
    return {
      cells: setCell(cells, activeIndex, null),
      activeIndex: moveIndex(activeIndex, -1, count),
    };
  }
  return state;
}

/** A paste replaces every cell and focuses the last one. */
export function reducePaste(text: string, count: number): DigitCellsState {
  return { cells: toCells(text, count), activeIndex: count - 1 };
}

/** The digit an `input` event leaves in a cell: its last character, if that is a digit. */
export function cellInputDigit(value: unknown): DigitCell {
  const digit = String(value ?? '').slice(-1);

  return DIGIT_REGEXP.test(digit) ? parseInt(digit) : null;
}
